import React, { useRef, useEffect, Fragment } from 'react';
import styles from './styles.scss';
import classnames from 'classnames';

function isElementInside(parentElement, element) {
  while (element) {
    if (element === parentElement) {
      return true;
    }
    element = element.parentElement;
  }
  return false;
}

export type PopupProps = {
  target: React.ReactNode;
  children: React.ReactNode;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  popupWidth?: number | string;
  onMouseEnter?: (string) => void;
  onMouseLeave?: (string) => void;

  position?: {
    top?: number;
    bottom?: number;
    left?: number;
    right?: number;
  };
  tabFocusable?: boolean;
  noPadding?: boolean,
};

const Popup: React.FunctionComponent<PopupProps> = props => {
  const {
    target,
    children,
    open,
    onOpen,
    onClose,
    onMouseEnter,
    onMouseLeave,
    popupWidth,
    position = { top: 40, left: 0 },
    tabFocusable = true,
    noPadding=false,
  } = props;

  let { top, left, bottom, right } = position;

  const wrapperRef = useRef<HTMLDivElement | null>(null);

  // When the popup is focused, focus the wrapper div
  useEffect(() => {
    if (!wrapperRef.current) {
      return;
    }

    if (open && !isElementInside(wrapperRef.current, document.activeElement)) {
      (wrapperRef.current as HTMLElement).focus();
    } else if (!open && document.activeElement === wrapperRef.current) {
      (wrapperRef.current as HTMLElement).blur();
    }
  }, [open]);

  return (
    <Fragment>
      {open ? (
        <div
          className={styles.backdrop}
          onMouseDown={e => {
            e.preventDefault();
            e.stopPropagation();
            onClose();
          }}
        />
      ) : null}

      <div
        ref={wrapperRef}
        className={styles.wrapper}
        tabIndex={tabFocusable ? 0 : -1}
        onFocus={e => {
          const focusedTarget = e.currentTarget;
          const bluredTarget = e.relatedTarget;
          if (
            isElementInside(wrapperRef.current, focusedTarget) &&
            !isElementInside(wrapperRef.current, bluredTarget)
          ) {
            onOpen();
          }
        }}
        onBlur={e => {
          const bluredTarget = e.currentTarget;
          const focusedTarget = e.relatedTarget;
          if (
            !isElementInside(wrapperRef.current, focusedTarget) &&
            isElementInside(wrapperRef.current, bluredTarget)
          ) {
            onClose();
          }
        }}
      >
        {/* Target */}
        <span
          role="button"
          className={classnames(styles.target, {
            [styles.focus]: open,
          })}
          onClick={onOpen}
          onMouseEnter={e => !open && onMouseEnter && onMouseEnter(e)}
          onMouseLeave={e => onMouseLeave && onMouseLeave(e)}
          data-test-id="target"
        >
          {target}
        </span>

        {/* Popup */}
        <div
          className={classnames(styles.popup, { [styles.open]: open, [styles.padding]: !noPadding })}
          style={{ width: popupWidth, top, bottom, left, right }}
          aria-hidden={!open}
        >
          {children}
        </div>
      </div>
    </Fragment>
  );
};

export default Popup;
